import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivityEntityName, ItemTaskCount, Task, TaskLinkType } from '@jms/model';
import { EntityDataService } from '@workbuddy/services/entities/entity.data.service';
import { BatchedKeyLoader } from '@workbuddy/utils/async';
import { Observable, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';

/** Payload for {@link SorTaskCountService.changes$}. `itemId: null` = entire cache busted. */
export interface SorTaskCountChange {
  itemId: string | null;
}

/**
 * Shared CountByItems cache for SOR completion / indicators cells and the item detail panel.
 *
 * - Cells call {@link getCount} as they scroll into view; same-tick ids batch into one command.
 * - After writes, prefer {@link invalidate} (clear + notify) so live cells re-fetch in place.
 * - Complete toggle can {@link patchMainTask} for an optimistic board update.
 * - {@link clear} drops cache only — use when the grid will recreate cells (`itemsChanged`).
 */
@Injectable({ providedIn: 'root' })
export class SorTaskCountService {

  private readonly entityData = inject(EntityDataService);

  private readonly changesSubject = new Subject<SorTaskCountChange>();
  public readonly changes$: Observable<SorTaskCountChange> = this.changesSubject.asObservable();

  private readonly loader = new BatchedKeyLoader<string, ItemTaskCount>({
    fetch: itemIds => this.entityData.executeCommand<ItemTaskCount[]>(ActivityEntityName.Task, 'CountByItems', { itemIds }),
    keyOf: count => count.itemId,
    fallback: itemId => ({ itemId, completed: 0, total: 0 }),
    ttlMs: 30_000,
  });

  public getCount(itemId: string): Promise<ItemTaskCount> {

    return this.loader.get(itemId);
  }

  public async getTasks(itemId: string): Promise<Task[]> {

    const result = await this.loader.get(itemId);
    return result?.tasks ?? [];
  }

  /** Cache only — no notify. Pair with grid row recreate. */
  public clear(itemId?: string): void {

    this.loader.clear(itemId);
  }

  /** Clear + notify so mounted cells re-load. Omit `itemId` to bust everything. */
  public invalidate(itemId?: string): void {

    this.loader.clear(itemId);
    this.changesSubject.next({ itemId: itemId ?? null });
  }

  /**
   * Optimistic parent item-task write (`TaskLinkType.Item`). Child `completed`/`total` unchanged.
   * Pass the saved task, not only its status: SOR Details reads the completion stamp from here.
   * No warm cache → {@link invalidate}.
   */
  public patchMainTask(itemId: string, saved: Partial<Task>): void {

    const current = this.loader.peek(itemId);
    if (!current) {
      this.invalidate(itemId);
      return;
    }

    const tasks = (current.tasks ?? []).map(task => (
      task?.type === TaskLinkType.Item ? { ...task, ...saved } : task
    ));

    this.loader.set(itemId, { ...current, tasks });
    this.changesSubject.next({ itemId });
  }
}

/**
 * Board cell helper: after first paint (`isActive`), re-run `onChange` when this item's tally
 * is invalidated or optimistically patched.
 */
export function watchSorTaskCount(taskCounts: SorTaskCountService, itemId: () => string | null | undefined, isActive: () => boolean, onChange: () => void, destroyRef: DestroyRef): void {

  taskCounts.changes$
    .pipe(
      filter(change => {
        if (!isActive()) return false;
        const id = itemId();
        return !!id && (!change.itemId || change.itemId === id);
      }),
      takeUntilDestroyed(destroyRef),
    )
    .subscribe(() => onChange());
}
